import { HttpEvent, HttpHandler, HttpRequest, HttpInterceptor, HttpContextToken } from '@angular/common/http';
import { catchError, Observable, of, throwError } from 'rxjs';
import { Injectable } from '@angular/core';
import { ErrorHandlerService } from '../error-handler.service';
import { GlobalsService } from '../globals.service';
import { environment } from '@env/environment';
import { NotificationSettings } from '@progress/kendo-angular-notification';
import { CommonFunctions } from '../../static/common-functions';
import { Router } from '@angular/router';
import { CommonValues } from '../../static/CommonValues';


export const EXCLUDE_NOTIFICATION_ONERROR = new HttpContextToken<boolean>(() => false);

@Injectable({
  providedIn: 'root'
})
export class ErrorInterceptor implements HttpInterceptor {
  private logoutInProgress = false;
  constructor(private errorHandlerService: ErrorHandlerService, private globals: GlobalsService, private router: Router) {
  }
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {

    return next.handle(req).pipe(
      catchError((errorResponse) => {
        const traceId = CommonFunctions.getGuid();
        const problemDetails = this.getResponseJson(errorResponse);
        console.error(`ErrorInterceptor: TraceId: ${traceId}`, req.urlWithParams, "HttpResponse:", errorResponse, "Problem Details:", problemDetails);
        errorResponse.message = problemDetails?.detail ?? errorResponse.message ?? 'An unknown error occurred';
        if (environment.debug) {
          const excludeNotificationOnError = req.context == null ? false : req.context.get(EXCLUDE_NOTIFICATION_ONERROR);
          const messages = ['Debug Error: Check console for details\n'];
          if (problemDetails) {
            if (typeof problemDetails === 'string') {
              messages.push(`Error: ${problemDetails}`);
              messages.push(`Status: ${errorResponse.status}`);
            } else {
              const issue = problemDetails?.title ?? 'Unknown issue';
              messages.push(`Issue: ${issue}`);
              if (problemDetails?.detail) {
                messages.push(`Error: ${problemDetails.detail ?? errorResponse.message ?? 'An unknown error occurred'}`);
              }
              messages.push(`ProblemId: ${problemDetails?.problemId}`);
              messages.push(`Status: ${problemDetails?.status ?? errorResponse.status}`);
            }
          } else {
            messages.push(`Error: ${errorResponse.message ?? 'An unknown error occurred'}`);
            messages.push(`Status: ${errorResponse.status}`);
          }
          messages.push(`TraceId: ${traceId}`);
          messages.push(`Url: ${req.urlWithParams}`);
          if (!excludeNotificationOnError) {
            this.globals.notifyError(messages.join('\n'), true, {
              cssClass: 'notification-multiline'
            } as NotificationSettings);
          }
        }
        this.errorHandlerService.emitError(errorResponse);
        if (this.checkIfUnauthorized(errorResponse)) {
          return of(null);
        }
        return throwError(() => errorResponse);
      })
    );
  }

  private checkIfUnauthorized(errorResponse: any): boolean {
    if (errorResponse.status === 403 && !this.logoutInProgress) {
      this.logoutInProgress = true;
      this.router.navigate(['/login']).then(() => {
        this.globals.notifyError(CommonValues.ErrorMessages_AccessDenied, true);
        setTimeout(() => {
          this.logoutInProgress = false;
        }, 1000);
      });
    }
    return errorResponse.status === 403;
  }
  getResponseJson(errorResponse): any | null {
    if (errorResponse.error instanceof ArrayBuffer) {
      if ('TextDecoder' in window) {
        // Decode as UTF-8
        var dataView = new DataView(errorResponse.error);
        var decoder = new TextDecoder('utf8');
        return JSON.parse(decoder.decode(dataView));
      } else {
        // Fallback decode as ASCII
        var decodedString = String.fromCharCode.apply(null, new Uint8Array(errorResponse.error));
        return JSON.parse(decodedString);
      }
    } else if (errorResponse.error?.title) {
      return errorResponse.error;
    }
    return null;
  }
}

